import React, { useCallback, useEffect, useState } from 'react'
import icons from '../assets/icons'
import Stopover from '../types/Stopover'
import useStopovers from '../hooks/useStopovers'
import useLanguage from '../hooks/useLanguage'
import { useTranslation } from 'react-i18next'

type PropsItem = {
  item: Stopover
  onClick: (item: Stopover) => void
}

const StopItem: React.FC<PropsItem> = ({ item, onClick }: PropsItem) => {
  const language = useLanguage()
  const [isHover, setIsHover] = useState(false)
  const { t } = useTranslation()

  const onMouseLeave = () => setIsHover(false)
  const onMouseEnter = () => setIsHover(true)

  const _onClick = useCallback(() => onClick(item), [item])

  return (
    <div
      onClick={_onClick}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      className='search--item'
      key={item.port_id}
      style={{ backgroundColor: isHover ? '#001024' : 'transparent' }}
    >
      <div
        className={`text text--size8 search--item--stop ${
          isHover ? 'search--item--stop--hover' : ''
        }`}
      >
        {t('pickers.stopover')}
      </div>
      <div
        className={`text text--size13 text--center search--item--label ${
          isHover ? 'text--white' : 'text--dark'
        }`}
      >
        {item.port_name[language]}
      </div>
      <div />
    </div>
  )
}

type Props = {
  onPickStop: (stopover: Stopover) => void

  visibleInput: boolean

  toggleInput: () => void
}

const isMobile = window.innerWidth < 1023

const SearchBar: React.FC<Props> = ({ onPickStop, visibleInput, toggleInput }: Props) => {
  const [searchValue, setSearchValue] = useState('')
  const [searchResult, setSearchResult] = useState<Stopover[]>([])
  const { searchStopovers } = useStopovers()
  const { t } = useTranslation()

  useEffect(() => {
    if (searchValue === '') {
      setSearchResult([])
    }
  }, [])

  const onChange: React.ChangeEventHandler<HTMLInputElement> = (event) => {
    setSearchValue(event.target.value)
    const result = searchStopovers(event.target.value)
    console.log(result)
    setSearchResult(result)
  }

  const onClickItem = useCallback(
    (item: Stopover) => {
      toggleInput()
      setSearchValue('')
      setSearchResult([])
      onPickStop(item)
    },
    [onPickStop, toggleInput],
  )

  const renderResult = useCallback(() => {
    if (searchResult.length > 0) {
      return (
        <>
          {searchResult.map((value) => (
            <StopItem key={value.port_id} item={value} onClick={onClickItem} />
          ))}
        </>
      )
    }
    return <p className='text text--size13 search--empty'>{t('pickers.empty')}</p>
  }, [searchResult])

  return (
    <div className={`search--layer ${searchValue.length > 0 ? 'search--layer--visible' : ''}`}>
      <div className='search--container'>
        <div className='select--firstrow'>
          {visibleInput && (
            <input
              style={isMobile ? { width: window.innerWidth - 100 } : undefined}
              className='search--input text'
              placeholder={t('pickers.search') || ''}
              value={searchValue}
              onChange={onChange}
            />
          )}
          <img
            src={visibleInput && isMobile ? icons.cross : icons.search}
            alt='search'
            className={`search--icon ${visibleInput ? 'search--icon--close' : ''}`}
            onClick={toggleInput}
          />
        </div>
      </div>
      <div className='search--result'>{searchValue.length > 0 && renderResult()}</div>
    </div>
  )
}

export default SearchBar
